/**
 * Workspace Schemas
 *
 * All Zod schemas for workspace operations including:
 * - Filesystem operations (read, write, list, delete, mkdir, stat)
 * - Search operations (search, index)
 * - Skills operations (list, get, search, references)
 */

import { z } from 'zod/v4';

// =============================================================================
// Filesystem Path Schemas
// =============================================================================

export const fsPathParams = z.object({
  path: z.string().describe('File or directory path (URL encoded)'),
});

export const workspaceIdPathParams = z.object({
  workspaceId: z.string().describe('Workspace ID'),
});

// =============================================================================
// Filesystem Query Schemas
// =============================================================================

export const fsReadQuerySchema = z.object({
  path: z.string().describe('Path to the file to read'),
  encoding: z.string().optional().describe('Encoding for text files (default: utf-8)'),
});

export const fsListQuerySchema = z.object({
  path: z.string().describe('Path to the directory to list'),
  recursive: z.coerce.boolean().optional().describe('Include subdirectories'),
});

export const fsStatQuerySchema = z.object({
  path: z.string().describe('Path to get info about'),
});

export const fsDeleteQuerySchema = z.object({
  path: z.string().describe('Path to delete'),
  recursive: z.coerce.boolean().optional().describe('Delete directories recursively'),
  force: z.coerce.boolean().optional().describe("Don't error if path doesn't exist"),
});

// =============================================================================
// Filesystem Body Schemas
// =============================================================================

export const fsWriteBodySchema = z.object({
  path: z.string().describe('Path to write to'),
  content: z.string().describe('Content to write (text or base64-encoded binary)'),
  encoding: z.enum(['utf-8', 'base64']).optional().default('utf-8').describe('Content encoding'),
  recursive: z.coerce.boolean().optional().describe('Create parent directories if needed'),
});

export const fsMkdirBodySchema = z.object({
  path: z.string().describe('Directory path to create'),
  recursive: z.coerce.boolean().optional().describe('Create parent directories if needed'),
});

// =============================================================================
// Filesystem Response Schemas
// =============================================================================

export const fileEntrySchema = z.object({
  name: z.string(),
  type: z.enum(['file', 'directory']),
  size: z.number().optional(),
  mount: z
    .object({
      provider: z.string(),
      icon: z.string().optional(),
      displayName: z.string().optional(),
      description: z.string().optional(),
      status: z
        .enum([
          'pending',
          'initializing',
          'ready',
          'starting',
          'running',
          'stopping',
          'stopped',
          'destroying',
          'destroyed',
          'error',
        ])
        .optional(),
      error: z.string().optional(),
    })
    .optional(),
});

export const fsReadResponseSchema = z.object({
  path: z.string(),
  content: z.string(),
  type: z.enum(['file', 'directory']),
  size: z.number().optional(),
  mimeType: z.string().optional(),
});

export const fsWriteResponseSchema = z.object({
  success: z.boolean(),
  path: z.string(),
});

export const fsListResponseSchema = z.object({
  path: z.string(),
  entries: z.array(fileEntrySchema),
});

export const fsDeleteResponseSchema = z.object({
  success: z.boolean(),
  path: z.string(),
});

export const fsMkdirResponseSchema = z.object({
  success: z.boolean(),
  path: z.string(),
});

export const fsStatResponseSchema = z.object({
  path: z.string(),
  type: z.enum(['file', 'directory']),
  size: z.number().optional(),
  createdAt: z.string().optional(),
  modifiedAt: z.string().optional(),
  mimeType: z.string().optional(),
});

// =============================================================================
// Search Schemas
// =============================================================================

export const searchQuerySchema = z.object({
  query: z.string().describe('Search query text'),
  topK: z.coerce.number().optional().default(5).describe('Maximum number of results'),
  mode: z.enum(['bm25', 'vector', 'hybrid']).optional().describe('Search mode'),
  minScore: z.coerce.number().optional().describe('Minimum relevance score threshold'),
});

export const searchResultSchema = z.object({
  id: z.string().describe('Document ID (file path)'),
  content: z.string(),
  score: z.number(),
  lineRange: z
    .object({
      start: z.number(),
      end: z.number(),
    })
    .optional(),
  scoreDetails: z
    .object({
      vector: z.number().optional(),
      bm25: z.number().optional(),
    })
    .optional(),
});

export const searchResponseSchema = z.object({
  results: z.array(searchResultSchema),
  query: z.string(),
  mode: z.enum(['bm25', 'vector', 'hybrid']),
});

export const indexBodySchema = z.object({
  path: z.string().describe('Path to use as document ID'),
  content: z.string().describe('Content to index'),
  metadata: z.record(z.string(), z.unknown()).optional().describe('Optional metadata'),
});

export const indexResponseSchema = z.object({
  success: z.boolean(),
  path: z.string(),
});

// =============================================================================
// Mount Schemas
// =============================================================================

export const mountInfoSchema = z.object({
  path: z.string().describe('Mount path'),
  provider: z.string().describe('Filesystem provider type'),
  readOnly: z.boolean().describe('Whether the mount is read-only'),
  displayName: z.string().optional().describe('Human-readable name'),
  icon: z.string().optional().describe('UI icon identifier'),
  name: z.string().optional().describe('Filesystem instance name'),
});

// =============================================================================
// Workspace Info Schema
// =============================================================================

export const workspaceInfoResponseSchema = z.object({
  isWorkspaceConfigured: z.boolean(),
  id: z.string().optional(),
  name: z.string().optional(),
  status: z.string().optional(),
  capabilities: z
    .object({
      hasFilesystem: z.boolean(),
      hasSandbox: z.boolean(),
      canBM25: z.boolean(),
      canVector: z.boolean(),
      canHybrid: z.boolean(),
      hasSkills: z.boolean(),
    })
    .optional(),
  safety: z
    .object({
      readOnly: z.boolean(),
    })
    .optional(),
  filesystem: z
    .object({
      id: z.string(),
      name: z.string(),
      provider: z.string(),
      status: z.string().optional(),
      error: z.string().optional(),
      readOnly: z.boolean().optional(),
      icon: z.string().optional(),
      metadata: z.record(z.string(), z.unknown()).optional(),
    })
    .optional(),
  mounts: z.array(mountInfoSchema).optional().describe('Mount points (only present for CompositeFilesystem)'),
});

const workspaceItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  source: z.enum(['mastra', 'agent']),
  agentId: z.string().optional(),
  agentName: z.string().optional(),
  capabilities: z.object({
    hasFilesystem: z.boolean(),
    hasSandbox: z.boolean(),
    canBM25: z.boolean(),
    canVector: z.boolean(),
    canHybrid: z.boolean(),
    hasSkills: z.boolean(),
  }),
  safety: z.object({
    readOnly: z.boolean(),
  }),
});

export const listWorkspacesResponseSchema = z.object({
  workspaces: z.array(workspaceItemSchema),
});

// =============================================================================
// Skills Path Parameter Schemas
// =============================================================================

export const skillNamePathParams = workspaceIdPathParams.extend({
  skillName: z.string().describe('Skill name identifier'),
});

export const skillReferencePathParams = skillNamePathParams.extend({
  referencePath: z.string().describe('Reference file path (URL encoded)'),
});

// Optional query param for disambiguating same-named skills
export const skillDisambiguationQuerySchema = z.object({
  path: z.string().optional().describe('Skill path for disambiguation when multiple skills share the same name'),
});

// =============================================================================
// Skills Query Parameter Schemas
// =============================================================================

export const searchSkillsQuerySchema = z.object({
  query: z.string().describe('Search query text'),
  topK: z.coerce.number().optional().default(5).describe('Maximum number of results'),
  minScore: z.coerce.number().optional().describe('Minimum relevance score threshold'),
  skillNames: z.string().optional().describe('Comma-separated list of skill names to search within'),
  includeReferences: z.coerce.boolean().optional().default(true).describe('Include reference files in search'),
});

// =============================================================================
// Skills Response Schemas
// =============================================================================

export const skillMetadataSchema = z.object({
  name: z.string(),
  description: z.string(),
  license: z.string().optional(),
  compatibility: z.unknown().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  path: z.string(),
});

export const skillSourceSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('external'), packagePath: z.string() }),
  z.object({ type: z.literal('local'), projectPath: z.string() }),
  z.object({ type: z.literal('managed'), mastraPath: z.string() }),
]);

export const skillSchema = skillMetadataSchema.extend({
  instructions: z.string(),
  source: skillSourceSchema,
  references: z.array(z.string()),
  scripts: z.array(z.string()),
  assets: z.array(z.string()),
});

/**
 * Source info for skills installed via skills.sh
 * Stored in .meta.json when a skill is installed
 */
export const skillsShSourceSchema = z.object({
  owner: z.string().describe('GitHub owner/org'),
  repo: z.string().describe('GitHub repository'),
});

export const skillMetadataWithPathSchema = skillMetadataSchema.extend({
  /** Source info for skills installed via skills.sh (from .meta.json) */
  skillsShSource: skillsShSourceSchema.optional(),
});

export const listSkillsResponseSchema = z.object({
  skills: z.array(skillMetadataWithPathSchema),
  isSkillsConfigured: z.boolean().describe('Whether skills are configured in the workspace'),
});

export const getSkillResponseSchema = skillSchema;

/**
 * Agent skill response schema - similar to skillSchema but with optional fields
 * for when full skill details aren't available (e.g., inherited skills without
 * direct access to the Skills instance).
 */
export const getAgentSkillResponseSchema = skillMetadataSchema.extend({
  path: z.string().optional(),
  instructions: z.string().optional(),
  source: skillSourceSchema.optional(),
  references: z.array(z.string()).optional(),
  scripts: z.array(z.string()).optional(),
  assets: z.array(z.string()).optional(),
});

export const skillReferenceResponseSchema = z.object({
  skillName: z.string(),
  referencePath: z.string(),
  content: z.string(),
});

export const listReferencesResponseSchema = z.object({
  skillName: z.string(),
  references: z.array(z.string()),
});

export const skillSearchResultSchema = z.object({
  skillName: z.string(),
  skillPath: z.string(),
  source: z.string(),
  content: z.string(),
  score: z.number(),
  lineRange: z
    .object({
      start: z.number(),
      end: z.number(),
    })
    .optional(),
  scoreDetails: z
    .object({
      vector: z.number().optional(),
      bm25: z.number().optional(),
    })
    .optional(),
});

export const searchSkillsResponseSchema = z.object({
  results: z.array(skillSearchResultSchema),
  query: z.string(),
});

// =============================================================================
// skills.sh Proxy Schemas
// =============================================================================

export const skillsShSearchQuerySchema = z.object({
  q: z.string().describe('Search query'),
  limit: z.coerce.number().optional().default(10).describe('Maximum number of results'),
});

export const skillsShPopularQuerySchema = z.object({
  limit: z.coerce.number().optional().default(10).describe('Maximum number of results'),
  offset: z.coerce.number().optional().default(0).describe('Offset for pagination'),
});

export const skillsShSkillSchema = z.object({
  id: z.string(),
  name: z.string(),
  installs: z.number(),
  topSource: z.string(),
});

export const skillsShSearchResponseSchema = z.object({
  query: z.string(),
  searchType: z.string(),
  skills: z.array(skillsShSkillSchema),
  count: z.number(),
});

export const skillsShListResponseSchema = z.object({
  skills: z.array(skillsShSkillSchema),
  count: z.number(),
  limit: z.number(),
  offset: z.number(),
});

export const skillsShPreviewQuerySchema = z.object({
  owner: z.string().describe('GitHub repository owner'),
  repo: z.string().describe('GitHub repository name'),
  path: z.string().describe('Path to skill within repo'),
});

export const skillsShPreviewResponseSchema = z.object({
  content: z.string(),
});

export const skillsShInstallBodySchema = z.object({
  owner: z.string().describe('GitHub repository owner'),
  repo: z.string().describe('GitHub repository name'),
  skillName: z.string().describe('Skill name from skills.sh'),
  mount: z.string().optional().describe('Mount path to install into (for CompositeFilesystem)'),
});

export const skillsShInstallResponseSchema = z.object({
  success: z.boolean(),
  skillName: z.string(),
  installedPath: z.string(),
  filesWritten: z.number(),
});

export const skillsShRemoveBodySchema = z.object({
  skillName: z.string().describe('Name of the installed skill to remove'),
});

export const skillsShRemoveResponseSchema = z.object({
  success: z.boolean(),
  skillName: z.string(),
  removedPath: z.string(),
});

export const skillsShUpdateBodySchema = z.object({
  skillName: z.string().optional().describe('Specific skill to update, or omit to update all'),
});

export const skillsShUpdateResponseSchema = z.object({
  updated: z.array(
    z.object({
      skillName: z.string(),
      success: z.boolean(),
      filesWritten: z.number().optional(),
      error: z.string().optional(),
    }),
  ),
});
